import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IAnnotationLandmark,
	ICreateAnnotationLandmarkThunkArgs,
	IDeleteAnnotationLandmarkThunkArgs,
	IGetAnnotationLandmarksThunkArgs,
	IUpdateAnnotationLandmarkThunkArgs,
} from 'src/features/annotation/domain/types';
import { annotationLandmarkApi } from 'src/features/annotation/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const ANNOTATION_LANDMARK_LOADING_KEY = 'annotation_landmark';

export const getAnnotationLandmarks = createAsyncThunk<
	IAnnotationLandmark[],
	IGetAnnotationLandmarksThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_landmark/getAnnotationLandmarks',
	async (
		{ annotationId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LANDMARK_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationLandmarkApi.getAnnotationLandmarks(
				annotationId,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch annotation landmarks',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LANDMARK_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const createAnnotationLandmark = createAsyncThunk<
	IAnnotationLandmark,
	ICreateAnnotationLandmarkThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_landmark/createAnnotationLandmark',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LANDMARK_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await annotationLandmarkApi.createAnnotationLandmark(
					request,
					siteSlug,
				);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to create annotation landmark',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LANDMARK_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const updateAnnotationLandmark = createAsyncThunk<
	IAnnotationLandmark,
	IUpdateAnnotationLandmarkThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_landmark/updateAnnotationLandmark',
	async (
		{ annotationId, request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LANDMARK_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await annotationLandmarkApi.updateAnnotationLandmark(
					annotationId,
					request,
					siteSlug,
				);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to update annotation landmark',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LANDMARK_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const deleteAnnotationLandmark = createAsyncThunk<
	void,
	IDeleteAnnotationLandmarkThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_landmark/deleteAnnotationLandmark',
	async (
		{ siteSlug, annotationId, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LANDMARK_LOADING_KEY,
					loading: true,
				}),
			);
			await annotationLandmarkApi.deleteAnnotationLandmark(
				siteSlug,
				annotationId,
			);

			onSuccess?.();
			return;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to delete annotation landmark',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LANDMARK_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
